import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PencilSimple, CurrencyEur } from "@phosphor-icons/react";
import ColorPickerField from "@/components/ColorPickerField";

/**
 * Compact user editor (admin only): name + phone + color.
 * Password reset lives in its own dialog. Role/teams have dedicated dialogs.
 */
export default function EditUserDialog({ open, onOpenChange, user, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      phone: user.phone || "",
      email: user.email || "",
      color: user.color || null,
      can_see_prices: !!user.can_see_prices,
    });
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Admin/compta always see prices — the toggle is irrelevant for them.
  const priceToggleApplies = user && user.role !== "admin" && user.role !== "compta";

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, form);
      toast.success("Équipier mis à jour");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <PencilSimple size={18} className="text-[#005CFF]" weight="duotone" /> Modifier {user.name}
          </DialogTitle>
          <DialogDescription className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
            Rôle {user.role} — infos personnelles
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Nom *</Label>
            <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10 h-10 mt-1" data-testid="edit-user-name" />
          </div>
          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Téléphone</Label>
            <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-white/5 border-white/10 h-10 mt-1" data-testid="edit-user-phone" />
          </div>
          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Email <span className="text-white/30 normal-case tracking-normal">(pour login Google + envoi identifiants)</span></Label>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="prenom.nom@bkpro.fr"
              value={form.email || ""}
              onChange={(e) => set("email", e.target.value)}
              className="bg-white/5 border-white/10 h-10 mt-1"
              data-testid="edit-user-email"
            />
          </div>
          <ColorPickerField
            value={form.color}
            onChange={(v) => set("color", v)}
            testid="edit-user-color"
            label="Couleur d'identification agenda"
          />

          {priceToggleApplies && (
            <button
              type="button"
              onClick={() => set("can_see_prices", !form.can_see_prices)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                form.can_see_prices
                  ? "bg-emerald-500/10 border-emerald-500/40"
                  : "bg-white/[0.02] border-white/10 hover:border-white/25"
              }`}
              data-testid="edit-user-can-see-prices"
              aria-pressed={form.can_see_prices}
            >
              <CurrencyEur size={16} weight="duotone" className={form.can_see_prices ? "text-emerald-400" : "text-white/50"} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold">Voir les prix et marges</div>
                <div className="text-[10px] text-white/50">
                  Accès aux devis chiffrés, tableau de rentabilité, montants factures.
                </div>
              </div>
              <div
                className={`w-9 h-5 rounded-full relative shrink-0 transition-colors ${
                  form.can_see_prices ? "bg-emerald-500" : "bg-white/15"
                }`}
              >
                <div
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                    form.can_see_prices ? "left-4" : "left-0.5"
                  }`}
                />
              </div>
            </button>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="flex-1 border border-white/10">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="edit-user-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
